import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Layout } from "@/components/layout";
import { useUser } from "@clerk/clerk-react";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FileText, Trash2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  useOrderTemplates,
  useDeleteOrderTemplate,
} from "@/hooks/use-order-templates";
import { useToast } from "@/hooks/use-toast";
import { OrderTemplateViewModal } from "@/components/orders/OrderTemplateViewModal";
import { OrderTemplate, TemplateProduct } from "@shared/schema";

export default function OrderTemplatesPage() {
  const { t } = useTranslation();
  const { user } = useUser();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [selectedTemplate, setSelectedTemplate] =
    useState<OrderTemplate | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Data hooks
  const { data: templates, isPending: isLoading } = useOrderTemplates();
  const deleteTemplateMutation = useDeleteOrderTemplate();

  const handleUseTemplate = (templateId: string) => {
    navigate(`/orders/new?template=${templateId}`);
  };

  const handleGoToEditPage = (templateId: string) => {
    navigate(`/admin/order-templates/edit?template=${templateId}`);
  };

  const handleViewTemplate = (template: OrderTemplate) => {
    setSelectedTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleDeleteTemplate = async (template: OrderTemplate) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteTemplateMutation.mutateAsync(template.uid);
        toast({
          title: t("pages.admin.orderTemplates.templateDeletedSuccessfully"),
        });
      } catch (error) {
        console.error("Error deleting template:", error);
        toast({
          title: t("pages.admin.orderTemplates.failedToDeleteTemplate"),
          description: t("pages.admin.orderTemplates.pleaseTryAgain"),
          variant: "destructive",
        });
      }
    }
  };

  // Template list view
  const TemplatesList = () => (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t("pages.admin.orderTemplates.orderTemplates")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("pages.admin.orderTemplates.manageYourOrderTemplates")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground mb-2">
            {t("pages.admin.orderTemplates.wantToCreateA")}
          </p>
          <Button onClick={() => navigate("/orders/new")}>
            {t("pages.admin.orderTemplates.createNewOrder")}
          </Button>
        </div>
      </div>

      <Separator />

      {isLoading ? (
        <div className="text-center p-8">{t("pages.admin.orderTemplates.loadingTemplates")}</div>
      ) : templates && templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((template) => {
              return (
                <Card
                  key={template.uid}
                  data-testid="order-template-card"
                  className="overflow-hidden hover:shadow-md transition-shadow"
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="line-clamp-1">
                        {template.name}
                      </CardTitle>
                      <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                        {t("common.template")}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="space-y-2 text-sm">
                      {/* Order Details */}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("pages.admin.orderTemplates.numberOfItems")}
                        </span>
                        <span className="font-medium">
                          {template.products && template.products.length > 0
                            ? template.products.reduce(
                                (sum: number, p: TemplateProduct) =>
                                  sum + (p.quantity || 1),
                                0,
                              )
                            : 0}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("pages.admin.orderTemplates.createdOn")}
                        </span>
                        <span className="text-sm">
                          {template.createdAt
                            ? new Date(template.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  month: "numeric",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : t("pages.admin.orderTemplates.nA")}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {t("pages.admin.orderTemplates.createdBy")}
                        </span>
                        <span className="text-sm">
                          {template.createdByName ||
                            user?.fullName ||
                            t("common.unknown")}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-3 flex flex-col gap-2">
                    <Button
                      size="sm"
                      className="w-full"
                      data-testid="use-order-template-button"
                      onClick={() => handleUseTemplate(template.uid)}
                    >
                      {t("pages.admin.orderTemplates.useOrderTemplate")}
                    </Button>
                    <div className="flex gap-2 w-full">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        data-testid="view-edit-template-button"
                        onClick={() => handleViewTemplate(template)}
                      >
                        {t("pages.admin.orderTemplates.viewEditTemplate")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              );
            })}
        </div>
      ) : (
        <div className="text-center p-12 border rounded-lg bg-muted/10">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-medium mb-2">{t("pages.admin.orderTemplates.noOrderTemplatesYet")}</h3>
          <p className="text-muted-foreground mb-4">
            {t("pages.admin.orderTemplates.orderTemplatesWillBe")}
          </p>
        </div>
      )}
    </>
  );

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("pages.admin.orderTemplates.orderTemplates")}</span>
      </div>
    }>
      <div className="space-y-6">
        <TemplatesList />
      </div>

      {/* Template Preview Modal */}
      <OrderTemplateViewModal
        template={selectedTemplate}
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setSelectedTemplate(null);
        }}
        onUseTemplate={(templateId) => {
          setPreviewModalOpen(false);
          setSelectedTemplate(null);
          handleGoToEditPage(templateId);
        }}
      />
    </Layout>
  );
}
